import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

interface Tetel {
  id: string; tipus: 'tanulo' | 'alkalmazott'; szemely: string; oktazon: string | null; nev: string | null;
  esemeny: string; esemeny_nev: string; reszlet: string; reszlet_nev: string; leiras: string | null;
  datum: string; hatarido: string; hatra_nap: number; jogalap: string | null; forras: string;
  forras_megvan: boolean; elso_atvizsgalas: boolean; allapot: string; allapot_kod: string; allapot_nev: string;
  bejelentve: string | null; bejelentette: string | null; bejelentes_datuma: string | null; kesve: boolean | null;
  megjegyzes: string | null; elozmeny: string | null; rogzitve: string; rogzitette: string | null;
  kir_azonosito_nelkul: boolean;
}

/**
 *  Admin → KIR-bejelentések (backend/kir-bejelentes.js).
 *
 *  A tanulói (és alkalmazotti) jogviszony létesítését, a KIR-ben nyilvántartott adatok változását és
 *  a jogviszony megszűnését öt napon belül be kell jelenteni a KIR-en keresztül (229/2012. Korm. r.
 *  14–17. §). A Sulinform a KIR-be nem küld adatot: ez a képernyő a teendőlista – mi bejelentendő,
 *  meddig, és ki jelölte bejelentettnek. A lista megnyitásakor a rendszer újra átnézi a jogviszonyokat.
 */
@Component({
  selector: 'app-admin-kir-bejelentes',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './kir-bejelentes.component.html',
  styleUrls: ['./kir-bejelentes.component.scss'],
})
export class AdminKirBejelentesComponent implements OnInit {
  intezmeny = '';
  tetelek: Tetel[] = [];
  darab: Record<string, number> = {};
  hatarido_nap = 5;
  tol = '';
  hiba = '';
  betolt = false;
  fut = false;

  szuro: 'nyitott' | 'lejart' | 'bejelentve' | 'nem_kell' | 'okafogyott' | 'kezdo' | 'mind' = 'nyitott';
  tipusSzuro: '' | 'tanulo' | 'alkalmazott' = '';
  kereses = '';
  kijelolt: Record<string, boolean> = {};
  bejelentesNapja = new Date().toLocaleDateString('sv-SE');
  ok = '';
  reszletNyitva: string | null = null;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.intezmeny = String((await this.data.getFromStorage('sajatIntezmeny'))?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    await this.betoltes();
  }

  async betoltes() {
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    this.betolt = true; this.hiba = '';
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/kir-bejelentes/lista/${this.intezmeny}`));
      this.tetelek = r?.tetelek || [];
      this.darab = r?.darab || {};
      this.hatarido_nap = r?.hatarido_nap || 5;
      this.tol = r?.tol || '';
      this.kijelolt = {};
    } catch (e: any) { this.hiba = e?.error?.error || 'A lista nem tölthető be.'; }
    finally { this.betolt = false; }
  }

  private nyitottE(t: Tetel) { return ['bejelentendo', 'lejar', 'lejart'].includes(t.allapot_kod); }

  get lathatok(): Tetel[] {
    const k = this.kereses.trim().toLowerCase();
    return this.tetelek.filter((t) => {
      if (this.tipusSzuro && t.tipus !== this.tipusSzuro) return false;
      if (k && !`${t.nev || ''} ${t.oktazon || ''} ${t.szemely}`.toLowerCase().includes(k)) return false;
      switch (this.szuro) {
        case 'nyitott': return this.nyitottE(t) && !t.elso_atvizsgalas;
        case 'lejart': return t.allapot_kod === 'lejart' && !t.elso_atvizsgalas;
        case 'kezdo': return t.elso_atvizsgalas && t.allapot === 'bejelentendo';
        case 'mind': return true;
        default: return t.allapot_kod === this.szuro;
      }
    });
  }

  szam(sz: string): number {
    const l = this.tetelek;
    switch (sz) {
      case 'nyitott': return l.filter((t) => this.nyitottE(t) && !t.elso_atvizsgalas).length;
      case 'lejart': return l.filter((t) => t.allapot_kod === 'lejart' && !t.elso_atvizsgalas).length;
      case 'kezdo': return l.filter((t) => t.elso_atvizsgalas && t.allapot === 'bejelentendo').length;
      case 'mind': return l.length;
      default: return l.filter((t) => t.allapot_kod === sz).length;
    }
  }

  get kijeloltIdk(): string[] { return Object.keys(this.kijelolt).filter((id) => this.kijelolt[id]); }
  get mindKijelolve(): boolean { const l = this.lathatok; return !!l.length && l.every((t) => this.kijelolt[t.id]); }
  mindetValt(be: boolean) { for (const t of this.lathatok) this.kijelolt[t.id] = be; }

  szin(t: Tetel): string {
    return ({ bejelentendo: 'primary', lejar: 'warning', lejart: 'danger', bejelentve: 'success', nem_kell: 'medium', okafogyott: 'medium' } as any)[t.allapot_kod] || 'medium';
  }

  hatraSzoveg(t: Tetel): string {
    if (!['bejelentendo', 'lejar', 'lejart'].includes(t.allapot_kod)) return '';
    if (t.hatra_nap < 0) return `${-t.hatra_nap} napja lejárt`;
    if (t.hatra_nap === 0) return 'ma jár le';
    return `még ${t.hatra_nap} nap`;
  }

  async jelol(allapot: 'bejelentve' | 'nem_kell' | 'bejelentendo') {
    const idk = this.kijeloltIdk;
    if (!idk.length) return;
    if (allapot === 'nem_kell' && !this.ok.trim()) { this.data.sendToast('Írd be, miért nem kell bejelenteni.', 'warning'); return; }
    if (allapot === 'bejelentendo' && !confirm(`${idk.length} tétel jelölését visszavonod – újra bejelentendő lesz. Folytatod?`)) return;
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/kir-bejelentes/jeloles/${this.intezmeny}`, {
        idk, allapot, datum: this.bejelentesNapja, megjegyzes: allapot === 'nem_kell' ? this.ok.trim() : undefined,
      }));
      this.data.sendToast(`${r?.modositva ?? 0} tétel jelölve.`, 'success');
      this.ok = '';
      await this.betoltes();
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'A jelölés nem sikerült.', 'error'); }
    finally { this.fut = false; }
  }

  elozmenySorok(t: Tetel): string[] { return String(t.elozmeny || '').split('\n').filter(Boolean); }
}
